import type { JSX, RefObject } from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Check, Loader2 } from 'lucide-react';
import { api, ApiError } from '@/lib/api-client';
import { ForbiddenNotice } from '@/components/shared/forbidden-notice';

const ENDPOINT = '/api/admin/system-prompt';

interface SystemPromptResponse {
  prompt: string;
  isDefault: boolean;
  maxLength: number;
  sha256: string;
  // Optional: against an older control plane the preview is hidden, not crashed.
  template?: { before: string; after: string };
}

interface SaveVariables {
  prompt: string;
  confirmWarnings: boolean;
  expectedSha256: string;
}

type SaveMutation = UseMutationResult<SystemPromptResponse, Error, SaveVariables>;

const VIOLATION_TEXTS: Record<string, string> = {
  TOO_LONG: 'hosszabb a megengedett 10 000 karakternél',
  CONTROL_CHARACTERS: 'nem megjeleníthető vezérlőkaraktert tartalmaz',
  BIDI_OVERRIDE: 'kétirányú írásirány-felülíró vagy láthatatlan karaktert tartalmaz',
  CUSTOMER_BLOCK_DELIMITER: 'a termék sablonjának blokkhatárolóját tartalmazza (<ugyfel-utasitasai>)',
};

// Mirrors renderSystemPrompt() in the control plane's services/system-prompt-template.ts.
const CUSTOMER_BLOCK_DELIMITER = /<\s*\/?\s*ugyfel-utasitasai\b[^>]*>/gi;

function stripCustomerBlockDelimiters(text: string): string {
  let stripped = text;
  let previous: string;
  do {
    previous = stripped;
    stripped = previous.replace(CUSTOMER_BLOCK_DELIMITER, '');
  } while (stripped !== previous);
  return stripped;
}

const WARNING_TEXTS: Record<string, string> = {
  CHAT_TEMPLATE_DELIMITER: 'chat-sablon határolót tartalmaz (például <|im_start|> vagy [INST])',
  JAILBREAK: 'ismert jailbreak-megnevezést tartalmaz (például „jailbreak" vagy „DAN mode")',
};

function detailCodes(error: unknown, field: 'violations' | 'warnings'): string[] {
  if (!(error instanceof ApiError)) return [];
  try {
    const parsed = JSON.parse(error.body) as Record<string, unknown>;
    const list = parsed[field];
    return Array.isArray(list) ? list.filter((item): item is string => typeof item === 'string') : [];
  } catch {
    return [];
  }
}

// Never String(error) here: ApiError's message is `<status> <statusText>: <raw body>`, so the
// pattern the other pages copy would render raw JSON instead of a readable sentence.
function saveErrorText(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return 'A mentés nem sikerült. Próbáld újra, és ha ismétlődik, jelezd az üzemeltetésnek.';
  }
  if (error.code === 'SYSTEM_PROMPT_INVALID_BODY') {
    return 'A beküldött adat alakja hibás. Töltsd újra az oldalt, és próbáld meg ismét.';
  }
  if (error.code === 'SYSTEM_PROMPT_REJECTED') {
    const reasons = detailCodes(error, 'violations').map((code) => VIOLATION_TEXTS[code] ?? code);
    return `A prompt nem menthető: ${reasons.join('; ')}.`;
  }
  if (error.code === 'SYSTEM_PROMPT_CONFLICT') {
    return 'A promptot időközben egy másik admin módosította. Töltsd be a friss szöveget, nézd át, és végezd el rajta újra a módosításod.';
  }
  return `A mentés nem sikerült (${error.status}). Próbáld újra, és ha ismétlődik, jelezd az üzemeltetésnek.`;
}

export function SystemPromptPage(): JSX.Element {
  const queryClient = useQueryClient();
  // Outlives the keyed editor's remount, so the page keeps its scroll position across a save.
  const scrollTopRef = useRef<number | null>(null);
  const { data, isLoading, error } = useQuery({
    queryKey: [ENDPOINT],
    queryFn: () => api.get<SystemPromptResponse>(ENDPOINT),
  });

  // Bumped per successful save: the saved text alone cannot tell "just saved" from "nothing
  // happened", because the endpoint trims and an empty save restores the shipped default.
  const [savedVersion, setSavedVersion] = useState(0);

  // The mutation lives here, above the keyed editor, so the "Mentve" feedback survives the
  // remount a successful save triggers.
  const save = useMutation({
    mutationFn: (variables: SaveVariables) => api.put<SystemPromptResponse>(ENDPOINT, variables),
    onSuccess: (result) => {
      // Seeded from the response so the remount below reads the new value, not a stale cache.
      queryClient.setQueryData([ENDPOINT], result);
      setSavedVersion((version) => version + 1);
      void queryClient.invalidateQueries({ queryKey: [ENDPOINT] });
    },
  });

  if (error instanceof ApiError && error.status === 403) return <ForbiddenNotice />;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-heading">System Prompt</h1>

      <div className="card space-y-3 p-5 text-sm">
        <p className="flex items-start gap-2 font-medium text-heading">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />A system prompt a szervezet
          AI-munkatársának alapszabályzata
        </p>
        <p>
          A mentett szöveg minden ezt követő LLM-hívással kimegy, tehát azonnal megváltoztatja az asszisztens
          viselkedését: a hangnemet, a nyelvet, a válaszadás korlátait. Az átírásnak megfelelőségi hatása is
          lehet, ezért a mentést megerősítés előzi meg.
        </p>
        <p>
          A rendszer csak azt ellenőrzi, ami eldönthető: a hosszt, a nem megjeleníthető vezérlőkaraktereket,
          és a láthatatlan karaktereket — az írásirány-felülírókat és a láthatatlan utasításblokkot is. Azt{' '}
          <strong>nem</strong> tudja megállapítani, hogy egy utasítás jó szándékú-e — ugyanaz a mondat lehet
          jogos szabály és visszaélés is.
        </p>
        <p>
          A platform biztonsági és megfelelőségi kontrolljait a prompt nem tudja kikapcsolni: a jogosultságok,
          az eszközpolitika, az adatmaszkolás, a költséghatárok és az auditnaplózás a prompton kívül élnek, és
          onnan nem elérhetők. Az itt mentett szöveg a termék sablonjába illeszkedik, egy{' '}
          <strong>körülhatárolt blokkba</strong>: a forrásmegjelölés, a pontosító visszakérdezés, a
          haladásjelzés, az elkészült anyagok és a hitelesítő adatok szabályait a sablon adja, és a blokkból a
          szöveg nem léphet ki. Az asszisztens szerepét és a szervezet nevét viszont az itt mentett szöveg
          adja meg, ezért áll a szállított alapérték első bekezdésében. Ha a szöveged mégis ellentmond
          valamelyik szabálynak, a sablon a termék szabályát mondja érvényesnek a modellnek. Beállítást a
          szöveg ettől még nem definiálhat.
        </p>
        <p>
          A lap alján lévő előnézet azt a teljes szöveget mutatja, amit az asszisztens ténylegesen megkap. Ha
          a mentett szövegedben olyan szabály is szerepel, amit a sablon már tartalmaz, az előnézetben kétszer
          fog látszani — ilyenkor nyugodtan töröld a sajátodból.
        </p>
        <p>
          <strong>Üres szöveg mentése visszaállítja a szállított alapértelmezett promptot.</strong> Ez az
          egyetlen visszaút az alapértékhez.
        </p>
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Betöltés…</p>}

      {/* Same slot as crud-table's error row, but a readable sentence: ApiError's message is
          `<status> <statusText>: <raw body>`, so String(error) would render raw JSON. */}
      {error != null && (
        <p className="flex items-start gap-2 text-sm text-rose-600">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> A system prompt betöltése nem sikerült. Töltsd
          újra az oldalt, és ha a hiba ismétlődik, jelezd az üzemeltetésnek.
        </p>
      )}

      {data && (
        <SystemPromptEditor
          key={`${savedVersion}:${data.prompt}`}
          data={data}
          save={save}
          scrollTopRef={scrollTopRef}
        />
      )}
    </div>
  );
}

function SystemPromptEditor({
  data,
  save,
  scrollTopRef,
}: {
  data: SystemPromptResponse;
  save: SaveMutation;
  scrollTopRef: RefObject<number | null>;
}): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [text, setText] = useState(data.prompt);
  const [confirming, setConfirming] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [pendingPath, setPendingPath] = useState<string | null>(null);
  const editorRef = useRef<HTMLTextAreaElement>(null);

  const isDirty = text !== data.prompt;
  const length = [...text].length;
  // The server's order: trim before storing, strip on render.
  const previewText = stripCustomerBlockDelimiters(text.trim());
  const renderedLength = data.template
    ? [...data.template.before].length + [...previewText].length + [...data.template.after].length
    : length;
  // Editing can no longer exceed the limit (onChange cuts), but a stored prompt can still
  // arrive over a maxLength lowered since it was saved — the guard stays for that load path.
  const overLimit = length > data.maxLength;
  const warnings =
    save.error instanceof ApiError && save.error.code === 'SYSTEM_PROMPT_WARNING'
      ? detailCodes(save.error, 'warnings')
      : [];
  const conflicted = save.error instanceof ApiError && save.error.code === 'SYSTEM_PROMPT_CONFLICT';

  // Retrying with the stale sha256 would only 409 again, so the way forward is the fresh text.
  // The refetched prompt remounts the keyed editor, which is what drops the conflicted draft.
  const loadFreshPrompt = () => {
    setConfirming(false);
    save.reset();
    void queryClient.invalidateQueries({ queryKey: [ENDPOINT] });
  };

  // Fit the content exactly, capped by max-h.
  useLayoutEffect(() => {
    const element = editorRef.current;
    if (!element) return;
    const scroller = element.closest('main');
    const scrollTop = scroller?.scrollTop;
    element.style.height = 'auto';
    const border = element.offsetHeight - element.clientHeight;
    element.style.height = `${element.scrollHeight + border}px`;
    if (scroller && scrollTop !== undefined) scroller.scrollTop = scrollTop;
  }, [text]);

  useLayoutEffect(() => {
    const scroller = editorRef.current?.closest('main');
    if (scroller && scrollTopRef.current !== null) scroller.scrollTop = scrollTopRef.current;
    return () => {
      scrollTopRef.current = scroller?.scrollTop ?? null;
    };
  }, [scrollTopRef]);

  // In-app navigation: capture phase, because a bubble-phase listener would run after
  // react-router's own handler and the navigation would already be under way. The draft lives
  // only in useState, so leaving the page drops it — that is what the ticket asks for.
  // Known limitation: the browser Back/Forward (popstate) is not covered — react-router's
  // useBlocker needs a data router, which this app does not use — so the draft is lost there
  // without a prompt (documented in docs/konfiguraciok.md).
  useEffect(() => {
    if (!isDirty) return undefined;
    const onClick = (event: MouseEvent) => {
      // A modifier or non-primary click opens a new tab or window — this page keeps its draft,
      // so there is nothing to guard.
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;
      const target = event.target as HTMLElement | null;
      const link = target?.closest?.('a[href]');
      if (!(link instanceof HTMLAnchorElement) || link.target === '_blank') return;
      const href = link.getAttribute('href');
      if (!href || !href.startsWith('/') || href === '/system-prompt') return;
      event.preventDefault();
      setPendingPath(href);
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [isDirty]);

  useEffect(() => {
    if (!isDirty) return undefined;
    const onBeforeUnload = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  return (
    <>
      <div className="card space-y-4 p-5">
        {data.isDefault && (
          <p className="text-sm text-muted-foreground">
            Ez a szállított alapértelmezett prompt — egyedi szöveget még nem mentettek.
          </p>
        )}

        <textarea
          ref={editorRef}
          className="input max-h-[70vh] resize-y overflow-y-auto font-mono"
          rows={1}
          value={text}
          spellCheck={false}
          onChange={(event) => {
            // AC5: the limit is enforced while editing, not only at save. Cut by code points —
            // the textarea's own maxLength counts UTF-16 units, which diverges from the server's
            // code-point count on emoji. The counter below still shows the limit being hit.
            const raw = event.target.value;
            const codePoints = [...raw];
            setText(codePoints.length > data.maxLength ? codePoints.slice(0, data.maxLength).join('') : raw);
            if (save.isSuccess || save.isError) save.reset();
          }}
        />

        <div className="flex items-center justify-between gap-4">
          <span className={`text-sm ${overLimit ? 'text-rose-600' : 'text-muted-foreground'}`}>
            {length.toLocaleString('hu-HU')} / {data.maxLength.toLocaleString('hu-HU')} karakter
          </span>
          <div className="flex items-center gap-3">
            {save.isSuccess && (
              <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <Check className="h-4 w-4" /> Mentve
              </span>
            )}
            <button
              className="btn-primary"
              disabled={!isDirty || overLimit || save.isPending}
              onClick={() => {
                setAcknowledged(false);
                save.reset();
                setConfirming(true);
              }}
            >
              Mentés
            </button>
          </div>
        </div>

        {save.isError && warnings.length === 0 && !confirming && (
          <div className="space-y-2">
            <p className="flex items-start gap-2 text-sm text-rose-600">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {saveErrorText(save.error)}
            </p>
            {conflicted && (
              <button className="btn-secondary" onClick={loadFreshPrompt}>
                Friss szöveg betöltése
              </button>
            )}
          </div>
        )}
      </div>

      {/* A <pre>, not a read-only <textarea>: a second `textbox` role would break the editor lookups. */}
      {data.template && (
        <div className="card space-y-3 p-5">
          <h2 className="text-sm font-medium text-heading">A modellnek kimenő teljes prompt</h2>
          <p className="text-sm text-muted-foreground">
            A termék sablonja a fenti szöveggel együtt — ez megy ki rendszer promptként. Csak olvasható, és
            gépelés közben frissül.
          </p>
          <pre className="max-h-[50vh] overflow-y-auto whitespace-pre-wrap font-mono text-xs">
            {`${data.template.before}${previewText}${data.template.after}`}
          </pre>
          <span className="text-sm text-muted-foreground">
            {renderedLength.toLocaleString('hu-HU')} karakter
          </span>
        </div>
      )}

      {confirming && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'var(--overlay)' }}
        >
          <div role="dialog" aria-modal="true" className="card w-full max-w-lg p-6">
            <h3 className="mb-3 text-lg font-semibold text-heading">System prompt mentése</h3>
            {warnings.length === 0 ? (
              <p className="text-sm">
                A mentés után az új szöveg minden LLM-hívással kimegy, és megváltoztatja az asszisztens
                viselkedését. A változásnak megfelelőségi hatása is lehet. Mented?
              </p>
            ) : (
              <div className="space-y-3 text-sm">
                <p className="flex items-start gap-2 text-amber-700">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />A szöveg olyan mintát tartalmaz, aminek
                  szervezeti promptban nincs helye:
                </p>
                <ul className="list-inside list-disc space-y-1">
                  {warnings.map((code) => (
                    <li key={code}>{WARNING_TEXTS[code] ?? code}</li>
                  ))}
                </ul>
                <label className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={acknowledged}
                    onChange={(event) => setAcknowledged(event.target.checked)}
                  />
                  <span>Tudomásul vettem, és a szöveget így akarom elmenteni.</span>
                </label>
              </div>
            )}
            {save.isError && warnings.length === 0 && (
              <p className="mt-4 flex items-start gap-2 text-sm text-rose-600">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {saveErrorText(save.error)}
              </p>
            )}
            <div className="mt-5 flex justify-end gap-2">
              <button
                className="btn-secondary"
                onClick={() => setConfirming(false)}
                disabled={save.isPending}
              >
                Mégse
              </button>
              {conflicted ? (
                <button className="btn-primary" onClick={loadFreshPrompt}>
                  Friss szöveg betöltése
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={() =>
                    save.mutate({
                      prompt: text,
                      confirmWarnings: warnings.length > 0,
                      expectedSha256: data.sha256,
                    })
                  }
                  disabled={save.isPending || (warnings.length > 0 && !acknowledged)}
                >
                  {save.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  Mentés
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {pendingPath && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'var(--overlay)' }}
        >
          <div role="dialog" aria-modal="true" className="card w-full max-w-lg p-6">
            <h3 className="mb-3 text-lg font-semibold text-heading">Nem mentett módosítás</h3>
            <p className="text-sm">
              A system promptot módosítottad, de nem mentetted el. Ha továbblépsz, a szerkesztett szöveg
              elveszik.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => setPendingPath(null)}>
                Vissza
              </button>
              <button
                className="btn-primary"
                onClick={() => {
                  const target = pendingPath;
                  setPendingPath(null);
                  void navigate(target);
                }}
              >
                Tovább mentés nélkül
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
